import React, { useEffect, useState } from "react";
import "../admin-affiliate-mobile.css";
import { Pie } from "react-chartjs-2";
import { Chart, ArcElement, Tooltip, Legend } from "chart.js";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase-config";
Chart.register(ArcElement, Tooltip, Legend);

export default function AdminDashboard() {
  // MINDEN HOOK A KOMPONENS ELEJÉN!
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [modalUser, setModalUser] = useState(null);
  const [modalAmount, setModalAmount] = useState("");
  const [modalNote, setModalNote] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");
  const [editModalUser, setEditModalUser] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  // Handle user status toggle
  const handleToggleUserStatus = async (userId, newStatus) => {
    try {
      const updateUserStatus = httpsCallable(functions, 'updateUserStatus');
      await updateUserStatus({ userId, isActive: newStatus });
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, isActive: newStatus } : user
      ));
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Hiba történt a felhasználó státuszának frissítésekor.');
    }
  };

  // Handle user deletion
  const handleDeleteUser = async (userId) => {
    if (!confirm('Biztosan törölni szeretnéd ezt a felhasználót? Ez a művelet nem visszavonható!')) {
      return;
    }
    
    try {
      const deleteUser = httpsCallable(functions, 'deleteUser');
      await deleteUser({ userId });
      
      // Remove from local state
      setUsers(prev => prev.filter(user => user.id !== userId));
      alert('Felhasználó sikeresen törölve!');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Hiba történt a felhasználó törlésekor: ' + error.message);
    }
  };

  // Handle manual email verification
  const handleVerifyEmail = async (userId, currentStatus) => {
    try {
      const manuallyVerifyUser = httpsCallable(functions, 'manuallyVerifyUser');
      await manuallyVerifyUser({ userId, emailVerified: !currentStatus });
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, emailVerified: !currentStatus } : user
      ));
      alert(`Email ${!currentStatus ? 'megerősítve' : 'megerősítés visszavonva'}!`);
    } catch (error) {
      console.error('Error verifying email:', error);
      alert('Hiba történt az email megerősítésekor.');
    }
  };

  // Handle user edit
  const handleEditUser = (user) => {
    setEditModalUser(user);
    setEditFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'affiliate',
      isActive: user.isActive !== false
    });
  };

  // Save user edits
  const handleSaveUserEdit = async () => {
    if (!editModalUser) return;
    
    setEditLoading(true);
    try {
      const updateUserStatus = httpsCallable(functions, 'updateUserStatus');
      await updateUserStatus({
        userId: editModalUser.id,
        ...editFormData
      });
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === editModalUser.id ? { ...user, ...editFormData } : user
      ));
      
      alert('Felhasználó adatai sikeresen frissítve!');
      setEditModalUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Hiba történt a felhasználó frissítésekor.');
    } finally {
      setEditLoading(false);
    }
  };

  // Handle balance update
  const handleUpdateBalance = async () => {
    if (!modalUser || !modalAmount) return;
    
    setModalLoading(true);
    setModalError("");
    setModalSuccess("");
    
    try {
      const updateUserStatus = httpsCallable(functions, 'updateUserStatus');
      const newBalance = (modalUser.balance || 0) + parseInt(modalAmount);
      
      await updateUserStatus({ 
        userId: modalUser.id, 
        balance: newBalance 
      });
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === modalUser.id ? { ...user, balance: newBalance } : user
      ));
      
      setModalSuccess('Jutalék sikeresen hozzáadva!');
      setTimeout(() => {
        setModalUser(null);
      }, 1500);
    } catch (error) {
      console.error('Error updating balance:', error);
      setModalError('Hiba történt a jutalék hozzáadásakor.');
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    async function fetchUsers() {
      setLoading(true);
      setError("");
      try {
        console.log('🔍 Fetching users from Firebase Function...');
        const getUsersForAdmin = httpsCallable(functions, 'getUsersForAdmin');
        const result = await getUsersForAdmin();
        
        console.log('📊 Firebase Function response:', result.data);
        
        if (result.data.success) {
          console.log(`✅ Loaded ${result.data.users.length} users from Firebase Function`);
          console.log('👥 Users data:', result.data.users);
          setUsers(result.data.users);
        } else {
          console.error('Function returned error:', result.data);
          setError("Nem sikerült betölteni a felhasználók listáját.");
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setError("Nem sikerült betölteni a felhasználók listáját: " + err.message);
      }
      setLoading(false);
    }
    fetchUsers();
  }, []);

  if (loading) return <div className="flex justify-center items-center min-h-[50vh]"><div className="loader"></div></div>;
  if (error) return <div className="text-red-600 text-center mt-10">{error}</div>;

  // Összesített statisztika
  const affiliates = users.filter(u => u.role !== "admin");
  const totalRegistrations = affiliates.reduce((sum, u) => sum + (u.affiliates?.length || 0), 0);
  const totalClicks = affiliates.reduce((sum, u) => sum + (u.totalClicks || 0), 0);
  const totalCommission = affiliates.reduce((sum, u) => sum + (u.balance || 0), 0);

  const pieData = {
    labels: ["Regisztrációk", "Kattintások", "Jutalék (Ft)",],
    datasets: [
      {
        data: [totalRegistrations, totalClicks, totalCommission],
        backgroundColor: ["#2563eb", "#fbbf24", "#22c55e"],
        borderColor: ["#1e40af", "#b45309", "#166534"],
        borderWidth: 2,
      },
    ],
  };

  // Keresés referralCode-ra
  const filteredUsers = search ? users.filter(u => (u.referralCode || "").toLowerCase().includes(search.toLowerCase())) : users;

  // CSV export
  function exportToCSV() {
    const csvRows = [];
    csvRows.push([
      "Név", "Email", "Referral kód", "Ki által ajánlott", "Meghívottak száma", "Kattintások", "Jutalék (Ft)", "Státusz"
    ].join(","));
    filteredUsers.forEach(u => {
      csvRows.push([
        `"${u.fullName || ""}"`,
        `"${u.email || ""}"`,
        `"${u.referralCode || ""}"`,
        `"${u.referredBy || ""}"`,
        u.affiliates?.length ?? 0,
        u.totalClicks ?? 0,
        u.balance ?? 0,
        u.role === "admin" ? "Admin" : "Aktív"
      ].join(","));
    });
    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "affiliates.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  // PDF export placeholder
  function exportToPDF() {
    alert("PDF export funkció fejlesztés alatt!");
  }

  // Dátum szűrő
  const now = new Date();
  let filteredByDate = affiliates;
  if (dateFilter === "7") {
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    filteredByDate = affiliates.filter(u => u.joinedAt && u.joinedAt.toDate() >= weekAgo);
  } else if (dateFilter === "30") {
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    filteredByDate = affiliates.filter(u => u.joinedAt && u.joinedAt.toDate() >= monthAgo);
  }
  const registrationsByPeriod = filteredByDate.length;

  // Aktív/inaktív userek száma
  const activeUsers = affiliates.filter(u => !u.isBanned).length;
  const inactiveUsers = affiliates.filter(u => u.isBanned).length;

  async function handleAddCommission() {
    setModalLoading(true);
    setModalError("");
    setModalSuccess("");
    try {
      // Firestore update: hozzáadjuk a commissionHistory-hoz
      
      const prevHistory = modalUser.commissionHistory || [];
      const newEntry = {
        sourceUserId: "admin",
        date: new Date(),
        amount: Number(modalAmount),
        note: modalNote,
        level: 0
      };
      const newHistory = [...prevHistory, newEntry];
      
      await updateDoc(doc(db, "users", modalUser.id), {
        commissionHistory: newHistory,
        balance: (modalUser.balance || 0) + Number(modalAmount)
      });
      setModalSuccess("Jutalék sikeresen hozzáadva!");
      setTimeout(() => setModalUser(null), 1200);
    } catch (e) {
      setModalError("Nem sikerült hozzáadni a jutalékot: " + e.message);
    }
    setModalLoading(false);
  }

  return (
    <div className="w-full max-w-7xl mx-auto bg-[#222831] bg-opacity-95 rounded-xl sm:rounded-2xl shadow-2xl border border-white/10 p-3 sm:p-6 md:p-10 mt-4 sm:mt-8 flex flex-col items-center gap-4 sm:gap-8 text-white relative admin-dashboard-mobile">
      {/* Kilépés gomb */}
      <button
        className="absolute top-2 right-2 sm:top-5 sm:right-5 bg-red-600 hover:bg-red-700 text-white font-bold px-3 py-1.5 sm:px-5 sm:py-2 rounded-full shadow-lg transition-all duration-200 z-10 text-xs sm:text-sm"
        onClick={() => { window.location.href = "/"; }}
        title="Kilépés a dashboardból"
      >
        Kilépés
      </button>
      <div className="flex items-center justify-between w-full">
        <h1 className="text-xl sm:text-3xl md:text-4xl font-extrabold text-hopp-red drop-shadow mb-1 flex items-center gap-2 mt-6 sm:mt-0">
          <svg className="w-6 h-6 sm:w-8 sm:h-8 text-hopp-red" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M13 16h-1v-4h-1m1-4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/></svg>
          Admin Dashboard
        </h1>
        <a
          href="/admin/user-management"
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded-lg shadow-lg transition-all duration-200 text-sm"
        >
          🚀 Fejlett Felhasználókezelés
        </a>
      </div>

      {/* Összesített statisztika és kördiagram */}
      <div className="flex flex-col lg:flex-row w-full gap-4 justify-between items-center mt-1 mb-1">
        <div className="flex flex-col gap-1 w-full lg:w-1/2">
          <div className="text-sm sm:text-base font-bold mb-1">Összesített statisztikák</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-base mb-1">
            <div className="text-center sm:text-left"><span className="font-semibold">Regisztrációk:</span> <span className="text-blue-400 font-bold">{totalRegistrations}</span></div>
            <div className="text-center sm:text-left"><span className="font-semibold">Kattintások:</span> <span className="text-yellow-400 font-bold">{totalClicks}</span></div>
            <div className="text-center sm:text-left"><span className="font-semibold">Jutalék (Ft):</span> <span className="text-green-400 font-bold">{totalCommission.toLocaleString()}</span></div>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm mt-2">
            <div className="text-center sm:text-left"><span className="font-semibold">Összes user:</span> <span className="text-blue-400 font-bold">{users.length}</span></div>
            <div className="text-center sm:text-left"><span className="font-semibold">Aktív userek:</span> <span className="text-green-400 font-bold">{activeUsers}</span></div>
            <div className="text-center sm:text-left"><span className="font-semibold">Inaktív userek:</span> <span className="text-red-400 font-bold">{inactiveUsers}</span></div>
          </div>
        </div>
        <div className="w-full lg:w-1/2 max-w-[120px] sm:max-w-[180px]">
          <Pie
            data={{
              labels: ["Aktív", "Inaktív"],
              datasets: [
                {
                  data: [activeUsers, inactiveUsers],
                  backgroundColor: ["#4caf50", "#f44336"],
                  borderColor: "#222",
                  borderWidth: 2,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: true,
                  position: "bottom",
                  labels: {
                    color: "#fff",
                    font: { size: 10 }
                  }
                }
              }
            }}
          />
        </div>
      </div>
      {/* Dátum szűrő és időszakos regisztrációk */}
      <div className="w-full flex flex-col lg:flex-row gap-4 items-center mb-4 mt-2">
        <div className="flex flex-col sm:flex-row gap-2 items-center">
          <span className="font-semibold text-xs sm:text-sm text-center">Regisztrációk időszak szerint:</span>
          <div className="flex gap-1 sm:gap-2">
            <button className={`px-2 py-1 rounded text-xs sm:text-sm ${dateFilter==="all"?"bg-hopp-red text-white":"bg-gray-200 text-black"}`} onClick={()=>setDateFilter("all")}>Összes</button>
            <button className={`px-2 py-1 rounded text-xs sm:text-sm ${dateFilter==="7"?"bg-hopp-red text-white":"bg-gray-200 text-black"}`} onClick={()=>setDateFilter("7")}>7 nap</button>
            <button className={`px-2 py-1 rounded text-xs sm:text-sm ${dateFilter==="30"?"bg-hopp-red text-white":"bg-gray-200 text-black"}`} onClick={()=>setDateFilter("30")}>30 nap</button>
          </div>
        </div>
        <div className="text-xs sm:text-base font-semibold text-center">Regisztrációk ebben az időszakban: <span className="text-blue-400 font-bold">{registrationsByPeriod}</span></div>
      </div>

      {/* Keresőmező */}
      <div className="w-full flex flex-col items-center mb-4">
        <input
          type="text"
          className="border px-3 py-2 rounded-lg text-black w-full max-w-md text-sm"
          placeholder="Keresés affiliate/referral kódra..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <div className="w-full overflow-x-auto mt-2">
        <table className="min-w-full text-left text-xs border rounded-xl overflow-hidden bg-[#2d3748] text-white">
          <thead>
            <tr className="bg-hopp-red text-white">
              <th className="px-1 sm:px-2 py-1 text-xs">Név</th>
              <th className="px-1 sm:px-2 py-1 text-xs hidden sm:table-cell">Email</th>
              <th className="px-1 sm:px-2 py-1 text-xs">Kód</th>
              <th className="px-1 sm:px-2 py-1 text-xs hidden md:table-cell">Referrer</th>
              <th className="px-1 sm:px-2 py-1 text-xs">Reg.</th>
              <th className="px-1 sm:px-2 py-1 text-xs hidden sm:table-cell">Katt.</th>
              <th className="px-1 sm:px-2 py-1 text-xs">Jutalék</th>
              <th className="px-1 sm:px-2 py-1 text-xs">Email</th>
              <th className="px-1 sm:px-2 py-1 text-xs">Státusz</th>
              <th className="px-1 sm:px-2 py-1 text-xs">Műveletek</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(u => (
              <tr key={u.id} className="border-b hover:bg-[#394867]/70">
                <td className="px-1 sm:px-4 py-1 sm:py-2 font-semibold text-xs">{(u.name || u.fullName || '').substring(0, 10)}{(u.name || u.fullName || '').length > 10 ? '...' : ''}</td>
                <td className="px-1 sm:px-4 py-1 sm:py-2 text-xs hidden sm:table-cell">{u.email?.substring(0, 20)}{u.email?.length > 20 ? '...' : ''}</td>
                <td className="px-1 sm:px-4 py-1 sm:py-2 font-mono text-xs">{(u.partnerCode || u.referralCode || '-').substring(0, 8)}</td>
                <td className="px-1 sm:px-4 py-1 sm:py-2 text-xs hidden md:table-cell">{(u.referredBy ?? '-').substring(0, 8)}</td>
                <td className="px-1 sm:px-2 py-1 text-center text-xs">{u.affiliates?.length ?? 0}</td>
                <td className="px-1 sm:px-2 py-1 text-center text-xs hidden sm:table-cell">{u.totalClicks ?? 0}</td>
                <td className="px-1 sm:px-2 py-1 text-right text-xs">{(u.balance ?? 0).toLocaleString()}</td>
                <td className="px-1 sm:px-2 py-1">
                  <button 
                    className={`text-xs px-1 py-0.5 rounded font-bold ${
                      u.emailVerified ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                    }`}
                    onClick={() => handleVerifyEmail(u.id, u.emailVerified)}
                    title={u.emailVerified ? 'Email megerősítve - kattints a visszavonáshoz' : 'Email nincs megerősítve - kattints a megerősítéshez'}
                  >
                    {u.emailVerified ? '✅' : '❌'}
                  </button>
                </td>
                <td className="px-1 sm:px-2 py-1">
                  {u.role === "admin"
                    ? <span className="inline-block px-2 py-0.5 rounded-full bg-blue-600 text-white text-xs font-bold">Admin</span>
                    : u.isActive !== false 
                      ? <span className="inline-block px-2 py-0.5 rounded-full bg-green-500 text-white text-xs font-bold">Aktív</span>
                      : <span className="inline-block px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold">Inaktív</span>
                  }
                </td>
                <td className="px-1 sm:px-2 py-1">
                  <div className="flex flex-wrap gap-1">
                    <button 
                      className="bg-blue-500 hover:bg-blue-600 text-xs text-white px-1 py-1 rounded font-bold" 
                      onClick={() => handleEditUser(u)}
                      title="Szerkesztés"
                    >
                      ✏️
                    </button>
                    <button 
                      className="bg-yellow-500 hover:bg-yellow-600 text-xs text-white px-1 py-1 rounded font-bold" 
                      onClick={() => { setModalUser(u); setModalAmount(""); setModalNote(""); setModalError(""); setModalSuccess(""); }}
                      title="Jutalék hozzáadása"
                    >
                      💰
                    </button>
                    {u.role !== "admin" && (
                      <>
                        <button 
                          className={`text-xs text-white px-1 py-1 rounded font-bold ${
                            u.isActive !== false ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-500 hover:bg-green-600'
                          }`}
                          onClick={() => handleToggleUserStatus(u.id, u.isActive !== false ? false : true)}
                          title={u.isActive !== false ? 'Deaktiválás' : 'Aktiválás'}
                        >
                          {u.isActive !== false ? '⏸️' : '▶️'}
                        </button>
                        <button 
                          className="bg-red-600 hover:bg-red-700 text-xs text-white px-1 py-1 rounded font-bold"
                          onClick={() => handleDeleteUser(u.id)}
                          title="Törlés"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-6 w-full sm:w-auto">
        <button
          onClick={exportToCSV}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 sm:px-6 rounded-xl text-xs sm:text-base shadow-lg transition-all duration-300 active:scale-95"
        >
          📊 CSV export
        </button>
        <button
          onClick={exportToPDF}
          className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 sm:px-6 rounded-xl text-xs sm:text-base shadow-lg transition-all duration-300 active:scale-95"
        >
          📄 PDF export
        </button>
      </div>
      {/* Jutalék hozzáadása modal */}
      {modalUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-sm sm:w-[340px] flex flex-col gap-3 relative max-h-[90vh] overflow-y-auto">
            <button className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl" onClick={()=>setModalUser(null)}>&times;</button>
            <h2 className="text-base sm:text-lg font-bold text-hopp-red mb-2">Jutalék hozzáadása</h2>
            <div className="text-xs sm:text-sm mb-1 font-semibold break-words">{modalUser.name || modalUser.fullName} ({modalUser.email})</div>
            <input type="number" className="border px-3 py-2 rounded w-full text-sm" placeholder="Összeg (Ft)" value={modalAmount} onChange={e=>setModalAmount(e.target.value)} min={1} />
            <input type="text" className="border px-3 py-2 rounded w-full text-sm" placeholder="Megjegyzés (opcionális)" value={modalNote} onChange={e=>setModalNote(e.target.value)} />
            {modalError && <div className="text-red-600 text-xs">{modalError}</div>}
            {modalSuccess && <div className="text-green-700 text-xs">{modalSuccess}</div>}
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <button className="bg-hopp-red text-white px-4 py-2 rounded font-bold disabled:opacity-60 text-sm" disabled={modalLoading || !modalAmount} onClick={handleUpdateBalance}>
                {modalLoading ? "Mentés..." : "Mentés"}
              </button>
              <button className="bg-gray-300 text-gray-800 px-4 py-2 rounded font-bold text-sm" disabled={modalLoading} onClick={()=>setModalUser(null)}>Mégse</button>
            </div>
          </div>
        </div>
      )}

      {/* Felhasználó szerkesztése modal */}
      {editModalUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-md flex flex-col gap-3 relative max-h-[90vh] overflow-y-auto">
            <button className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl" onClick={()=>setEditModalUser(null)}>&times;</button>
            <h2 className="text-base sm:text-lg font-bold text-hopp-red mb-2">Felhasználó szerkesztése</h2>
            
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Név</label>
                <input 
                  type="text" 
                  className="border px-3 py-2 rounded w-full text-sm" 
                  value={editFormData.name || ''} 
                  onChange={e => setEditFormData(prev => ({...prev, name: e.target.value}))}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input 
                  type="email" 
                  className="border px-3 py-2 rounded w-full text-sm" 
                  value={editFormData.email || ''} 
                  onChange={e => setEditFormData(prev => ({...prev, email: e.target.value}))}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefonszám</label>
                <input 
                  type="tel" 
                  className="border px-3 py-2 rounded w-full text-sm" 
                  value={editFormData.phone || ''} 
                  onChange={e => setEditFormData(prev => ({...prev, phone: e.target.value}))}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Szerepkör</label>
                <select 
                  className="border px-3 py-2 rounded w-full text-sm" 
                  value={editFormData.role || 'affiliate'} 
                  onChange={e => setEditFormData(prev => ({...prev, role: e.target.value}))}
                >
                  <option value="affiliate">Affiliate</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="isActive" 
                  checked={editFormData.isActive !== false} 
                  onChange={e => setEditFormData(prev => ({...prev, isActive: e.target.checked}))}
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">Aktív felhasználó</label>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button 
                className="bg-hopp-red text-white px-4 py-2 rounded font-bold disabled:opacity-60 text-sm" 
                disabled={editLoading} 
                onClick={handleSaveUserEdit}
              >
                {editLoading ? "Mentés..." : "Mentés"}
              </button>
              <button 
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded font-bold text-sm" 
                disabled={editLoading} 
                onClick={()=>setEditModalUser(null)}
              >
                Mégse
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


